import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, CreditCard, FileDown, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";

const ICONS = {
  COMPLETED: <CheckCircle2 size={48} className="mx-auto text-brand-teal" />,
  CANCELED: <XCircle size={48} className="mx-auto text-rose-500" />,
  PENDING: <Clock size={48} className="mx-auto text-amber-500" />,
  UNPAID: <CreditCard size={48} className="mx-auto text-brand-teal" />,
};

export default function Payment() {
  const { t } = useT();
  useSeo({ title: "Płatność za pakiet", path: ROUTES.pay, noindex: true });
  const [params] = useSearchParams();
  const order = params.get("order");
  const token = params.get("t");
  const paidFlag = params.get("paid");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (paidFlag && !order) return undefined;
    if (!order || !token) {
      setError(true);
      return undefined;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get("/payments/status", { params: { order, t: token } });
        setStatus(r.data);
        const s = r.data.payment_status;
        if ((s === "PENDING" || s === "WAITING_FOR_CONFIRMATION") && tries < 10) {
          tries += 1;
          timer = setTimeout(poll, 3000);
        }
      } catch {
        setError(true);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [order, token, t.payment.page_title]);

  const s = status?.payment_status;
  const unpaid = s === "NONE" || s === "CANCELED";
  const kind = s === "COMPLETED" ? "COMPLETED" : s === "CANCELED" ? "CANCELED" : s === "NONE" ? "UNPAID" : "PENDING";
  const copy = t.payment[kind.toLowerCase()];
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order}/contract.pdf?t=${token}`;
  const proformaUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order}/proforma.pdf?t=${token}`;

  const [quote, setQuote] = useState(null);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    if (!unpaid) return;
    api.get("/payments/quote", { params: { order, t: token } }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [unpaid, order, token]);

  const payNow = async () => {
    setPaying(true);
    try {
      const r = await api.post("/payments/payu/create", { order_id: order, t: token });
      window.location.assign(r.data.redirectUri);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(err.response?.status === 429 ? t.errors.too_many : typeof d === "string" ? d : t.order.pay_err);
      setPaying(false);
    }
  };

  return (
    <div data-testid="payment-page" className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Wirtualne biuro Warszawa — Twoje Biuro, strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
          <Link to="/" data-testid="payment-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {t.footer.back_home}
          </Link>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 text-center">
          {paidFlag && !order ? (
            <>
              <CheckCircle2 size={48} className="mx-auto text-brand-teal" />
              <h1 data-testid="payment-title" className="font-display font-bold text-2xl mt-5">{t.payment.completed.title}</h1>
              <p data-testid="payment-status" data-status="COMPLETED" className="text-sm text-muted-foreground mt-3 leading-relaxed">{t.payment.completed.sub}</p>
              <Link to="/" className="mt-8 inline-flex items-center justify-center bg-brand-teal text-white font-semibold px-8 py-3 rounded-full text-sm hover:bg-brand-tealDark transition-colors">
                {t.footer.back_home}
              </Link>
            </>
          ) : error ? (
            <>
              <XCircle size={48} className="mx-auto text-rose-500" />
              <h1 data-testid="payment-title" className="font-display font-bold text-2xl mt-5">{t.payment.error_title}</h1>
              <p className="text-sm text-muted-foreground mt-3">{t.payment.error_sub}</p>
            </>
          ) : !status ? (
            <Loader2 data-testid="payment-loading" className="animate-spin mx-auto text-slate-500" size={32} />
          ) : (
            <>
              {ICONS[kind]}
              <h1 data-testid="payment-title" className="font-display font-bold text-2xl mt-5">{copy.title}</h1>
              <p data-testid="payment-status" data-status={s} className="text-sm text-muted-foreground mt-3 leading-relaxed">{copy.sub}</p>
              {status.package_name && (
                <p className="text-xs text-muted-foreground mt-4">
                  {t.order.package}: <strong>{status.package_name}</strong>
                  {status.amount ? <> · {(status.amount / 100).toFixed(2).replace(".", ",")} zł</> : null}
                </p>
              )}
              {unpaid && quote && (
                <div data-testid="payment-pay-box" className="mt-6 rounded-2xl bg-slate-100 dark:bg-slate-800 p-5 text-left">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t.order.pay_title}</p>
                  <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm">{quote.description}</span>
                    <span data-testid="payment-pay-amount" className="font-display font-bold text-xl">{quote.amount_label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t.order.pay_gross}</p>
                  <button
                    data-testid="payment-pay-now-button"
                    onClick={payNow}
                    disabled={paying}
                    className="mt-4 w-full bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-8 py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {paying ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
                    {paying ? t.order.pay_redirect : t.order.pay_now}
                  </button>
                </div>
              )}
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <a data-testid="payment-download-pdf" href={pdfUrl} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors">
                  <FileDown size={15} /> {t.order.pdf_download}
                </a>
                <a data-testid="payment-download-proforma" href={proformaUrl} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors">
                  <FileDown size={15} /> {t.order.proforma_download}
                </a>
                <Link to="/" className="inline-flex items-center justify-center bg-brand-teal text-white font-semibold px-8 py-3 rounded-full text-sm hover:bg-brand-tealDark transition-colors">
                  {t.footer.back_home}
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
